import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    IconButton,
    Typography,
    Box,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Tooltip,
    Chip,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import Axios from '../../REST/base';
import PageShell from '../../themes/components/PageShell';
import PageHeader from '../../themes/components/PageHeader';
import PageContent from '../../themes/components/PageContent';
import Action from '../../themes/components/Action';
import Access from '../../Utils/authorize';

export default function ImageLibrary() {
    const navigate = useNavigate();
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const fileInputRef = useRef(null);

    const canEdit = Access.isAdmin('Settings');

    useEffect(() => {
        (async () => {
            try {
                const resp = await Axios().get('ImageLibrary');
                setImages(Array.isArray(resp.data) ? resp.data : []);
            } catch (err) {
                console.log('Failed to load images', err);
            }
        })();
    }, [refresh]);

    const handleUploadClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await Axios('Image uploaded').post('ImageLibrary', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setRefresh(r => !r);
        } catch (err) {
            console.log('Upload failed', err);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this image? Any templates using it will need to be updated.')) return;
        try {
            await Axios().delete('ImageLibrary/' + id);
            setRefresh(r => !r);
        } catch (err) {
            console.log('Delete failed', err);
        }
    };

    const handleCopyUrl = (image) => {
        navigator.clipboard.writeText(image.url);
        setCopiedId(image._id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const formatSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    return (
        <PageShell>
            <PageHeader
                title="Image Library"
                back
                onBack={() => navigate(-1)}
                actions={
                    canEdit && (
                        <Action
                            primary
                            icon={<AddPhotoAlternateIcon />}
                            label={uploading ? 'Uploading...' : 'Upload Image'}
                            onClick={handleUploadClick}
                            disabled={uploading}
                        />
                    )
                }
            />
            <PageContent>
                {/* Hidden file input */}
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />

                {images.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 10,
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            cursor: canEdit ? 'pointer' : 'default'
                        }}
                        onClick={canEdit ? handleUploadClick : undefined}
                    >
                        <AddPhotoAlternateIcon sx={{ fontSize: 60, color: '#bbb', mb: 2 }} />
                        <Typography color="text.secondary">
                            No images yet.{canEdit ? ' Click to upload your first image.' : ''}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {images.map((img) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={img._id}>
                                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardMedia
                                        component="img"
                                        height="160"
                                        image={img.url}
                                        alt={img.name}
                                        sx={{ objectFit: 'contain', bgcolor: '#f5f5f5', p: 1 }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                    <CardContent sx={{ flexGrow: 1, pb: 0 }}>
                                        <Typography variant="subtitle2" noWrap title={img.name}>
                                            {img.name}
                                        </Typography>
                                        {img.size && (
                                            <Chip
                                                label={formatSize(img.size)}
                                                size="small"
                                                variant="outlined"
                                                sx={{ mt: 0.5, fontSize: '0.7rem' }}
                                            />
                                        )}
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: 'block',
                                                mt: 1,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: '0.65rem'
                                            }}
                                            title={img.url}
                                        >
                                            {img.url}
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'space-between', pt: 0 }}>
                                        <Tooltip title={copiedId === img._id ? 'Copied!' : 'Copy URL to use in templates'}>
                                            <Button
                                                size="small"
                                                startIcon={copiedId === img._id ? <CheckIcon /> : <ContentCopyIcon />}
                                                color={copiedId === img._id ? 'success' : 'primary'}
                                                onClick={() => handleCopyUrl(img)}
                                            >
                                                {copiedId === img._id ? 'Copied!' : 'Copy URL'}
                                            </Button>
                                        </Tooltip>
                                        {canEdit && (
                                            <Tooltip title="Delete image">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(img._id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </PageContent>
        </PageShell>
    );
}
